'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import type { AuthProps, MenuItem } from '@govinity/ui/components/blocks/navbar/types';
import { PortalNavigation } from '@govinity/ui/components/ui-custom/portal-navigation';
import { PORTAL_BANNER_HEIGHT } from '@govinity/shared-config';
import type { PublicGovernmentResponse } from '@govinity/validation/public';
import { getAvailableSearchCategories } from '@/lib/search-categories';
import { useContainerDimensions } from '@/lib/hooks/use-container-dimensions';
import { useImageDimensions } from '@/lib/hooks/use-image-dimensions';
import { useScreenSize } from '@/lib/hooks/use-screen-size';
import { FollowGovernmentButton } from './follow-government-button';
import { PortalHeroSearch } from './portal-hero-search';

/** Extra hero height (px) added when the search overlay is shown, for a taller banner. */
const HERO_SEARCH_EXTRA_HEIGHT = 64;

type CropConfig = {
  x: number;
  y: number;
  width: number;
  height: number;
};

const getCropStyles = (
  crop: CropConfig | undefined,
  imageDimensions: { width: number; height: number } | null,
  containerWidth: number,
  containerHeight: number
): React.CSSProperties => {
  if (!imageDimensions || !crop || containerWidth === 0 || containerHeight === 0) {
    return { backgroundSize: 'cover', backgroundPosition: 'center' };
  }

  const { x, y, width, height } = crop;
  const scale = Math.max(containerWidth / width, containerHeight / height);
  const scaledCropWidth = width * scale;

  let backgroundPositionX: string;
  if (scaledCropWidth > containerWidth) {
    const cropCenterX = (x + width / 2) * scale;
    const containerCenterX = containerWidth / 2;
    backgroundPositionX = `${containerCenterX - cropCenterX}px`;
  } else {
    backgroundPositionX = `${-x * scale}px`;
  }

  return {
    backgroundSize: `${imageDimensions.width * scale}px ${imageDimensions.height * scale}px`,
    backgroundPosition: `${backgroundPositionX} ${-y * scale}px`,
  };
};

interface PortalHeroBannerProps {
  menu: MenuItem[];
  auth: AuthProps;
  userAvatarSlot?: React.ReactNode;
  showThemeToggle?: boolean;
  title?: string | null;
  subtitle?: string | null;
  government: PublicGovernmentResponse;
}

export const PortalHeroBanner = ({
  menu,
  auth,
  userAvatarSlot,
  showThemeToggle = false,
  title,
  subtitle,
  government,
}: PortalHeroBannerProps) => {
  const t = useTranslations('PortalHeroBanner');
  const screenSize = useScreenSize();
  const { ref: containerRef, dimensions: containerDimensions } = useContainerDimensions();
  const hasSearchCategories =
    government.hasAgenda === true && getAvailableSearchCategories(government).length > 0;

  const showSearch = hasSearchCategories;
  const heroHeight = showSearch
    ? PORTAL_BANNER_HEIGHT + HERO_SEARCH_EXTRA_HEIGHT
    : PORTAL_BANNER_HEIGHT;

  const bannerUrl = government.bannerUrl;
  const imageDimensions = useImageDimensions(bannerUrl || '');
  const bannerCustomizations = government.customizations?.banner;
  const customNavLink = government.customizations?.customNavLink;
  const cropConfig = bannerCustomizations?.[screenSize];
  const cropStyles = getCropStyles(
    cropConfig,
    imageDimensions,
    containerDimensions.width,
    heroHeight
  );

  const displayTitle = title ?? government.customizations?.headerText ?? t('defaultTitle');
  const displaySubtitle =
    subtitle ?? government.customizations?.subheaderText ?? t('defaultSubtitle');

  const logoImageUrl = government.logoUrl || '/images/blue-gov-logo.png';
  const logoClickUrl = government.customizations?.logoClickUrl || `/portal/${government.code}`;
  const logoLinkProps = government.customizations?.logoClickUrl
    ? { target: '_blank', rel: 'noopener noreferrer' }
    : undefined;

  return (
    <div>
      {/* Hero — banner image (or solid fallback) with the search overlaid on top. */}
      {(bannerUrl || showSearch) && (
        <div className="relative" style={{ height: `${heroHeight}px` }}>
          {bannerUrl ? (
            <div
              ref={containerRef}
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${bannerUrl})`, ...cropStyles }}
              aria-hidden="true"
            />
          ) : (
            <div className="bg-muted absolute inset-0" aria-hidden="true" />
          )}
          <div
            className="absolute inset-0 bg-linear-to-b from-slate-900/30 via-transparent to-slate-900/20"
            aria-hidden="true"
          />

          {/* Search — overlaid at the top: centered on mobile, anchored to the
              banner's right edge on larger screens. */}
          {showSearch && (
            <div className="absolute inset-x-0 top-0 flex justify-center px-8 pb-2 sm:justify-end sm:px-6 lg:px-8">
              <div className="w-full rounded-b-lg bg-slate-900/40 p-2 sm:w-auto">
                <PortalHeroSearch governmentCode={government.code} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Logo + Title Bar — logo overlaps hero */}
      <section
        className="border-border bg-background relative border-b"
        aria-labelledby="portal-hero-banner-title"
      >
        <div className="mx-auto max-w-7xl px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-5">
            <Link
              href={logoClickUrl}
              aria-label={t(logoLinkProps ? 'logoLinkNewTabAriaLabel' : 'logoLinkAriaLabel', {
                governmentName: government.name,
              })}
              {...logoLinkProps}
            >
              <Image
                src={logoImageUrl}
                alt={government.name}
                width={100}
                height={100}
                className={
                  bannerUrl
                    ? 'border-background bg-background -mt-12 h-[100px] w-[100px] shrink-0 rounded-lg border-4 object-cover shadow-md sm:-mt-14 sm:h-[110px] sm:w-[110px] md:-mt-16 md:h-[130px] md:w-[130px]'
                    : 'border-background bg-background h-[100px] w-[100px] shrink-0 rounded-lg border-4 object-cover shadow-md sm:h-[110px] sm:w-[110px] md:h-[130px] md:w-[130px]'
                }
              />
            </Link>
            <div className="min-w-0 flex-1">
              <h1
                id="portal-hero-banner-title"
                className="text-foreground line-clamp-2 text-lg font-semibold sm:text-xl md:line-clamp-1 md:text-2xl lg:text-3xl"
              >
                {displayTitle}
              </h1>
              <p className="text-muted-foreground mt-0.5 line-clamp-2 text-sm sm:text-base md:line-clamp-1 md:text-lg">
                {displaySubtitle}
              </p>
            </div>
            <FollowGovernmentButton className="w-full shrink-0 sm:w-auto" />
          </div>
        </div>
      </section>

      {/* Navigation Bar */}
      <PortalNavigation
        menu={menu}
        auth={auth}
        customNavLink={customNavLink}
        userAvatarSlot={userAvatarSlot}
        showThemeToggle={showThemeToggle}
      />
    </div>
  );
};
